/**
 * S3-05 (review 7): rebuild a Studio project from its WORKFLOW RECORD when this browser no longer holds it.
 *
 * WHY. "Back to Studio" on a /downloads card opens `?sid=<projectId>`, and the editor restores from `STUDIO-<projectId>`
 * in localStorage. Cleared site data, a quota eviction or another device leave no record, and the card said "This
 * project's files are no longer on our servers" two minutes after an export -- false: the uploads live on the edge for
 * 2.5 h. The edge's `POST /v2/edge/workflows/replay-history {fileIds:[uuid]}` returns, per workflow, a `rehydrate` block
 * (ironman `24b9684b`): `options` (the composition and the export summary), `inputs[]` (location, originalName, token,
 * metadata), `metadata` (the FIRST input's `ProcessorMetadata`), `uploadedFileIds[]`. That is everything a record holds
 * except each input's EDGE (the block names one), so each input is found with one `/files/input/info` on the candidate
 * edges -- the block's own, the card's, and the edges this browser has measured -- and a 404 everywhere means the file
 * really is gone. The same approach as the picker's `frame-picker/root/rehydrate.ts` (a3f131f27); none of its code.
 *
 * The rebuilt record carries each input's wire snapshot (S-44: `restoredUploadOf` re-creates the upload from it), so the
 * project exports again. Its revision is the exported one: exporting it unchanged is "this exact project was already
 * exported" (S3-02) and leads to the existing result; any edit mints the next revision as usual. NEVER THROWS.
 */
import { bearerHeaders, mintInputToken, ourEdgeHost } from '../../../utils/edge-owner';
import type { OriginalInputFile, ProcessorMetadata } from '../../../types/types';
import { exportSignature, fromWire, isValidProjectId } from '../model/composition';
import { harmoniseSummary } from '../export/build-request';
import type { StudioCompositionV1 } from '../model/composition';
import { clipEdgeBaseOf, extensionOf, kindOfExtension, uploadedFileIdOf } from './clip-source';
import { STUDIO_RECORD_RULES, STUDIO_RECORD_VERSION } from './persistence';
import type { StoredClipRef, StudioRecord } from './persistence';

/** The `rehydrate` block of one replay-history entry (ironman `ReplayRehydrate.kt`). Every piece may be absent. */
export interface StudioRehydrate {
    /**
     * R5 suite (J18 B): `uploadDomain` -- the edge that holds THIS input, when the server says so. Read first; without it
     * the input is looked for on the candidate edges (`locateInput`), whose wrong guesses answer 404.
     */
    inputs?: Array<{ location?: string; originalName?: string; token?: string; uploadDomain?: string; metadata?: Record<string, unknown> }>;
    options?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    uploadedFileIds?: string[];
    featurePage?: string;
    /**
     * WS-3 (review 7): the replay-history ENTRY's `lastUpdatedDate` (the job's own time), carried beside the block by
     * `fetchStudioRehydrate`. The rebuilt refs age from it -- never from the rebuild itself, which promised a project rebuilt
     * 2 h after its export another 2.5 h on files the edge was about to sweep.
     */
    lastUpdatedDate?: string;
}

export type FetchLike = (url: string, init?: { method?: string; headers?: Record<string, string>; body?: string; mode?: 'cors'; credentials?: 'omit' }) =>
    Promise<{ ok: boolean; status: number; json(): Promise<any> }>;

/** Why nothing was rebuilt: the edge had no Studio record for the job, or at least one file is gone from every edge. */
export type RehydrateFailure = 'no-record' | 'files-gone';

/** The URL keys a /downloads link uses to ask for a rebuild (see `rehydrateStudioHref`). */
export const REHYDRATE_WORKFLOW_PARAM = 'rwf';
export const REHYDRATE_DOMAIN_PARAM = 'rdom';

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** `https://host` for a domain given as a host, a URL or a URL with a path; null for anything else. */
export function edgeOrigin(domain: unknown): string | null {
    const raw = str(domain)?.trim();
    if (!raw) return null;
    try {
        const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
        // Review 7 SHARED-URL C-2: every origin here receives this browser's bearer (replay-history, the probe, the
        // token mint) -- so only one of OUR edges is ever an origin; `rdom`, a reply's `uploadDomain`, a measured edge
        // off the allow-list is null, and null means no request.
        return url.hostname && ourEdgeHost(url.hostname) ? `https://${url.hostname}` : null;
    } catch {
        return null;
    }
}

/**
 * The /downloads card's link that asks the Studio to rebuild its project -- for the DOWNLOADS lane's `reeditRouteOf`,
 * when this browser has no record for the card's project. `workflowUuid` is the card's `FileUpload.id` (what
 * replay-history is keyed by), `domain` its process/upload edge. Null when anything is missing.
 */
export function rehydrateStudioHref(pageSlug: string, projectId: string, workflowUuid: string, domain: string): string | null {
    const origin = edgeOrigin(domain);
    if (!pageSlug || !isValidProjectId(projectId) || !str(workflowUuid) || !origin) return null;
    const q = new URLSearchParams({ tab: 'studio', sid: projectId, [REHYDRATE_WORKFLOW_PARAM]: workflowUuid, [REHYDRATE_DOMAIN_PARAM]: origin });
    return `/${pageSlug}?${q.toString()}`;
}

/** The replay-history entry's `rehydrate` block for one workflow, or null. */
export async function fetchStudioRehydrate(workflowUuid: string, domain: string, fetchFn: FetchLike): Promise<StudioRehydrate | null> {
    const origin = edgeOrigin(domain);
    if (!origin) return null;
    try {
        const url = `${origin}/v2/edge/workflows/replay-history`;
        const res = await fetchFn(url, {
            method: 'POST', mode: 'cors', credentials: 'omit',
            // Review 7 SHARED-URL: the block is answered to the workflow's OWNER -- the bearer names this browser,
            // and (C-2) is built only for an allow-listed edge URL.
            headers: { 'Content-Type': 'application/json', ...bearerHeaders(url) },
            body: JSON.stringify({ fileIds: [workflowUuid] }),
        });
        if (!res.ok) return null;
        const body = obj(await res.json());
        const entry = obj(body?.[workflowUuid]);
        const block = obj(entry?.rehydrate) as StudioRehydrate | null;
        if (!block) return null;
        const updated = str(entry?.lastUpdatedDate);
        return updated ? { ...block, lastUpdatedDate: updated } : block;
    } catch {
        return null;
    }
}

/** The first candidate edge whose `/files/input/info` knows `location`; `'gone'` when every one answered 404/410. */
export async function locateInput(location: string, candidates: readonly string[], fetchFn: FetchLike, token?: string | null): Promise<string | 'gone' | null> {
    let unsure = false;
    for (const origin of candidates) {
        try {
            const url = `${origin}/v2/edge/files/input/info`;
            const res = await fetchFn(url, {
                method: 'POST', mode: 'cors', credentials: 'omit',
                headers: { 'Content-Type': 'application/json', ...bearerHeaders(url) },
                body: JSON.stringify({ filename: location, ...(token ? { token } : {}) }),
            });
            if (res.ok) return origin;
            if (res.status !== 404 && res.status !== 410) unsure = true;
        } catch {
            unsure = true;
        }
    }
    return unsure ? null : 'gone';
}

/** The composition inside `rehydrate.options`, or null when this workflow is not a Studio export. */
export function compositionOf(rh: StudioRehydrate | null | undefined): StudioCompositionV1 | null {
    const options = obj(rh?.options);
    const raw = options?.composition;
    const c = (typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw) as StudioCompositionV1 | null;
    return c && typeof c === 'object' && isValidProjectId((c as any).projectId) && Array.isArray((c as any).tracks) ? c : null;
}

/**
 * Pure: the block + each input's located edge → a `StudioRecord`, or null when it is not a Studio export or an input
 * lacks what the wire needs (location, token, an edge). The refs age from the entry's `lastUpdatedDate` (WS-3) --
 * the uploads are older, so the tab's TTL can only call them expired EARLIER than the truth, never later.
 */
export function studioRecordOf(
    rh: StudioRehydrate | null | undefined,
    edges: ReadonlyArray<string | null>,
    opts: { pageSlug: string; now: number; uniqueProcessType?: string },
): StudioRecord | null {
    const composition = compositionOf(rh);
    const inputs = rh?.inputs ?? [];
    if (!composition || inputs.length === 0) return null;
    const shared = { ...(obj(rh?.metadata) ?? {}) } as Record<string, unknown>;
    delete shared.fileMetadata;
    const clips: StoredClipRef[] = [];
    // WS-3: the job's own time, from the entry -- the uploads are older still, so the tab can only call them expired
    // EARLIER than the truth, never promise hours the edge no longer holds them for.
    const updatedMs = rh?.lastUpdatedDate ? Date.parse(rh.lastUpdatedDate) : NaN;
    const uploadedAt = Number.isFinite(updatedMs) && updatedMs > 0 ? Math.min(updatedMs, opts.now) : null;
    for (let i = 0; i < inputs.length; i += 1) {
        const input = inputs[i];
        const location = str(input?.location);
        const token = str(input?.token);
        const edge = edges[i] ?? null;
        if (!location || !token || !edge) return null;
        const meta = obj(input?.metadata) ?? {};
        const name = str(input?.originalName) ?? str(meta.fname) ?? location;
        const ext = extensionOf(name) || extensionOf(location);
        const kind = kindOfExtension(ext);
        if (!kind) return null;
        const uploadedFileId = str(rh?.uploadedFileIds?.[i]) ?? uploadedFileIdOf(location) ?? '';
        const size = Number(meta.fsize) || 0;
        const originalInputFile = {
            gcName: '', location, originalName: name, storageType: 'local', _id: uploadedFileId, token, metadata: { ...meta },
        } as unknown as OriginalInputFile;
        const processorMetadata = {
            ...shared,
            uploadDomain: edge,
            thumbnailDomain: edge,
            fileMetadata: { name, safeName: location, type: str(meta.ftype) ?? '', size: String(size) },
        } as unknown as ProcessorMetadata;
        clips.push({
            clipId: `rh${i}-${uploadedFileId || location}`.slice(0, 64),
            uploadedFileId,
            safeName: location,
            token,
            // R4-JE-02: the ref's `uploadDomain` is a RESOLVED base (`storedClipRefOf`'s contract) -- the same builder a
            // fresh upload's source goes through, never the bare origin the input was located on.
            uploadDomain: clipEdgeBaseOf(edge),
            name,
            size,
            ext,
            kind,
            ...(uploadedAt ? { uploadedAt } : {}),
            wire: { originalInputFile, processorMetadata, uniqueProcessType: opts.uniqueProcessType ?? '' },
        });
    }
    const options = { ...(obj(rh?.options) ?? {}) };
    delete (options as any).composition;
    delete (options as any).fileName;
    const typedName = str(obj(rh?.options)?.fileName);
    const doc = fromWire(composition);
    // JE-05 (acceptance R2, P1): the rebuilt record KNOWS what the server holds under `projectId#revision` -- the
    // exported submission. Without it the next build treated the project as never submitted and sent any EDIT under the
    // same key: 409, and the old video. With it, an edit is a new revision (a fresh render); an unchanged project is the
    // same signature and replays the export (or, where the signature cannot be reproduced exactly, renders anew).
    const inputIds = clips.map((c) => c.uploadedFileId);
    const signature = exportSignature(doc, inputIds, harmoniseSummary({ ...options }, doc.frame));
    return {
        v: STUDIO_RECORD_VERSION,
        kind: 'project',
        projectId: composition.projectId,
        pageSlug: opts.pageSlug || str(rh?.featurePage) || '',
        doc,
        clips,
        submission: { signature, revision: composition.revision },
        // WS-2 (review 7): the name the server holds the export under IS the submitted one (`options.fileName` is the
        // sanitised name the wire carried). Without it a rename after a rebuild kept the exported signature + revision
        // (the signature does not hash the name) -- 409, and the OLD video under the old name, against the X1 ruling.
        ui: {
            selectedClipId: null, scrollMs: 0, frameTouched: true, holdTouched: true,
            ...(typedName ? { fileName: typedName, submittedFileName: typedName } : {}),
        },
        savedAt: opts.now,
        generation: 1,
        rules: STUDIO_RECORD_RULES,
        options,
    };
}

export interface StudioRehydrateRequest {
    workflowUuid: string;
    domain: string;
    pageSlug: string;
    now: number;
    uniqueProcessType?: string;
    /** More edges to look for an input on (the block's and `domain` are tried first). */
    extraEdges?: readonly string[];
}

/** Fetch → locate every input → the record. Resolves to the record or the failure; never throws. */
export async function rehydrateStudioProject(req: StudioRehydrateRequest, fetchFn: FetchLike): Promise<StudioRecord | RehydrateFailure> {
    try {
        const rh = await fetchStudioRehydrate(req.workflowUuid, req.domain, fetchFn);
        if (!compositionOf(rh)) return 'no-record';
        const candidates = Array.from(new Set([
            edgeOrigin(obj(rh?.metadata)?.uploadDomain), edgeOrigin(req.domain), ...(req.extraEdges ?? []).map(edgeOrigin),
        ].filter((d): d is string => !!d)));
        const edges: Array<string | null> = [];
        // R5 suite (J18 B phone, "failed first-party request: 404 POST /files/input/info"): the rebuild looked for every
        // input on the candidates in a FIXED order, so each input on the second edge cost a 404 on the first. The files of
        // one project sit on a few edges, in runs -- the edge that answered last is asked first, and an input the server
        // names an edge for (`uploadDomain`) is looked for there first. A 404 is still possible (an input on an edge
        // nothing named) and is harmless: it only rules that edge out.
        let lastFound: string | null = null;
        // Review 7 SHARED-URL: the block carries no upload token any more; once an input is located, its OWNER mints one
        // from that edge (`/files/input/token`). A refusal (not this browser's file) is `no-record`, like a stranger's uuid.
        const inputs: NonNullable<StudioRehydrate['inputs']> = [];
        for (const input of rh?.inputs ?? []) {
            const location = str(input?.location);
            // F3f (review 7): the server now names each input's edge (`inputs[i].uploadDomain`) -- that edge and NO other
            // (the file lives there; asking elsewhere is only a 404). Without it (an older backend) the likely-edge order.
            const named = edgeOrigin(input?.uploadDomain ?? (obj(input?.metadata)?.uploadDomain as string | undefined));
            const order = named ? [named] : Array.from(new Set([lastFound, ...candidates].filter((d): d is string => !!d)));
            const where = location ? await locateInput(location, order, fetchFn, str(input?.token)) : null;
            if (where === 'gone') return 'files-gone';
            if (where) lastFound = where;
            edges.push(where);
            if (where && location && !str(input?.token)) {
                const token = await mintInputToken(where, location, fetchFn);
                if (!token) return 'no-record';
                inputs.push({ ...input, token });
            } else {
                inputs.push(input);
            }
        }
        return studioRecordOf(rh ? { ...rh, inputs } : rh, edges, { pageSlug: req.pageSlug, now: req.now, uniqueProcessType: req.uniqueProcessType }) ?? 'no-record';
    } catch {
        return 'no-record';
    }
}

/** The edges this browser has measured (`ConnectivityService`'s `ENDPOINT_RTT`), as origins. Empty when none. */
export function measuredEdges(storage: Pick<Storage, 'getItem'> | null | undefined): string[] {
    try {
        const raw = storage?.getItem('ConnectivityService-ENDPOINT_RTT');
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list)
            ? list.map((e: any) => edgeOrigin(e?.domain) ?? edgeOrigin(e?.ncfEndpoint)).filter((d: string | null): d is string => !!d)
            : [];
    } catch {
        return [];
    }
}
